import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Save, Eye, EyeOff, ArrowLeft } from 'lucide-react'
import { createStory, CreateStoryData } from '../api/stories'

const CreateStoryPage = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formData, setFormData] = useState<CreateStoryData>({
    title: '',
    content: '',
    excerpt: '',
    imageUrl: '',
    tags: [],
    isPublished: false
  })
  const [tagInput, setTagInput] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      await createStory(formData)
      navigate('/stories')
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }))
  }

  const addTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tagInput.trim()]
      }))
      setTagInput('')
    }
  }

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter(tag => tag !== tagToRemove) || []
    }))
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addTag()
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/stories')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Stories
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Write Your Story</h1>
          <p className="text-gray-600 mt-2">
            Share your experience with the community and inspire others.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6">
          <div className="space-y-6">
            {/* Title */}
            <div>
              <label htmlFor="title" className="form-label">
                Story Title *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                required
                className="input-field"
                placeholder="Enter your story title"
                value={formData.title}
                onChange={handleChange}
              />
            </div>

            {/* Excerpt */}
            <div>
              <label htmlFor="excerpt" className="form-label">
                Short Description
              </label>
              <textarea
                id="excerpt"
                name="excerpt"
                rows={3}
                className="input-field"
                placeholder="A brief summary of your story (optional)"
                value={formData.excerpt}
                onChange={handleChange}
              />
            </div>

            {/* Content */}
            <div>
              <label htmlFor="content" className="form-label">
                Story Content *
              </label>
              <textarea
                id="content"
                name="content"
                required
                rows={12}
                className="input-field"
                placeholder="Tell your story here..."
                value={formData.content}
                onChange={handleChange}
              />
            </div>

            {/* Image URL */}
            <div>
              <label htmlFor="imageUrl" className="form-label">
                Image URL (optional)
              </label>
              <input
                type="url"
                id="imageUrl"
                name="imageUrl"
                className="input-field"
                placeholder="https://example.com/image.jpg"
                value={formData.imageUrl}
                onChange={handleChange}
              />
            </div>

            {/* Tags */}
            <div>
              <label className="form-label">Tags</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  className="input-field flex-1"
                  placeholder="Add a tag"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                <button
                  type="button"
                  onClick={addTag}
                  className="btn-secondary"
                >
                  Add
                </button>
              </div>
              {formData.tags && formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-2 text-primary-600 hover:text-primary-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Publish Toggle */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPublished"
                name="isPublished"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                checked={formData.isPublished}
                onChange={handleCheckboxChange}
              />
              <label htmlFor="isPublished" className="ml-2 block text-sm text-gray-900">
                Publish immediately
              </label>
              <div className="ml-2 text-sm text-gray-500">
                {formData.isPublished ? (
                  <span className="flex items-center text-green-600">
                    <Eye className="h-4 w-4 mr-1" />
                    Will be published
                  </span>
                ) : (
                  <span className="flex items-center text-gray-500">
                    <EyeOff className="h-4 w-4 mr-1" />
                    Will be saved as draft
                  </span>
                )}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="text-red-600 text-sm">{error}</div>
            )}

            {/* Submit Button */}
            <div className="flex gap-4">
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex items-center"
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Saving...' : 'Save Story'}
              </button>
              
              <button
                type="button"
                onClick={() => navigate('/stories')}
                className="btn-outline"
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateStoryPage 